import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuBox, LuBuilding2, LuCircleHelp, LuDices, LuGrid3X3, LuLandmark, LuLocateFixed, LuMousePointerClick, LuRotateCcw, LuVolume2, LuVolumeX, LuZoomIn, LuZoomOut } from "react-icons/lu";
import { FeedbackBox, QuestionCard } from "../components/QuestionCard";
import { Modal } from "../components/ui";
import { specFromEquipped } from "../char3d/types";
import { GROUPS, SQUARES, priceOf, type MonopolyView } from "../../shared/games/monopoly";
import type { GameProps } from "../pages/Match";
import type { MonoStage, SfxName, StagePlayer } from "../monopoly3d/stage";
import { MonoSound, saveSoundPref, soundPref } from "../monopoly3d/sfx";
import { Stage3D } from "./mono/Stage3D";
import { Board2D } from "./mono/Board2D";
import { ActionBar, DeedCard, MODE_COLOR, MODE_LABEL, ModeBar, PlayerChip, PropertyList, TradeSummary, modeHelp, useHold, useMedia, type Mode } from "./mono/panels";
import { SEAT_COLOR, kr, paletteOf } from "./mono/common";

const FORCE2D_KEY = "edu-mono-2d";
const readForce2d = () => { try { return localStorage.getItem(FORCE2D_KEY) === "1"; } catch { return false; } };

/** what is going on, in words – the headline over the board and a hint underneath */
function headlineOf(o: { b: MonopolyView; you: number; names: (s: number) => string; over: boolean; winner: unknown; hasQuestion: boolean; held: boolean }): { title: string; hint: string } {
  const { b, you, names, over, winner, hasQuestion, held } = o;
  const sqName = (i: number) => SQUARES[i]!.name;
  const me = b.players[you]!;
  const myTurn = b.actor === you;
  if (over) return { title: winner === "draw" ? "Uafgjort!" : winner === you ? "Du vandt EduPoly!" : `${names(winner as number)} vandt EduPoly`, hint: "Flot spillet! Se hvordan det gik herunder." };
  if (held) {
    if (b.rolled) {
      const sum = b.dice[0] + b.dice[1];
      return { title: `${myTurn ? "Du slår" : `${names(b.actor)} slår`} ${b.dice[0]} + ${b.dice[1]} = ${sum}${b.dice[0] === b.dice[1] ? " – to ens!" : ""}`, hint: "" };
    }
    return { title: `${names(b.actor)} er i gang…`, hint: "" };
  }
  if (hasQuestion && b.q) {
    const k = b.q.kind;
    const title = ({ buy: `Du står på ${sqName(b.q.sq)}`, rent: `Du skal betale leje til ${names(b.q.to as number)}`, tax: `Du skal betale ${sqName(b.q.sq)}`, build: `Du vil bygge på ${sqName(b.q.sq)}`, jail: "Du vil ud af fængslet" } as Record<string, string>)[k] ?? "Svar på spørgsmålet";
    const half = kr(Math.ceil((b.q.amount ?? 0) / 2));
    const hint = ({ buy: "Svar rigtigt, så må du købe den. Svarer du forkert, sendes den på auktion.", rent: `Svar rigtigt og betal kun halv leje (${half} i stedet for ${kr(b.q.amount ?? 0)}).`, tax: `Svar rigtigt og betal kun halv skat (${half}).`, build: "Svar rigtigt for at bygge. Forkert svar = ingen flere bygninger i denne tur.", jail: "Svar rigtigt, så slipper du gratis ud." } as Record<string, string>)[k] ?? "";
    return { title, hint };
  }
  if (myTurn && b.stage === "auction" && b.auction) return { title: "Auktion – din tur til at byde", hint: `${sqName(b.auction.sq)} · højeste bud: ${b.auction.bidder === null ? "ingen" : `${kr(b.auction.bid)} (${names(b.auction.bidder)})`}` };
  if (myTurn) {
    const t = ({ roll: me.jail > 0 ? "Du sidder i fængsel" : "Din tur – slå med terningerne!", buy: `Vil du købe ${sqName(me.pos)} for ${kr(priceOf(me.pos))}?`, manage: "Du kan bygge, handle eller afslutte turen", debt: `Du skylder ${kr(b.debt?.total ?? 0)}`, auction: "Auktion – din tur til at byde", trade: "Nogen har et tilbud til dig" } as Record<string, string>)[b.stage] ?? "";
    return { title: t, hint: b.stage === "roll" && me.jail === 0 ? "Tryk på knappen – eller på terningerne." : b.stage === "manage" ? "Klik på dine felter for at bygge eller pantsætte." : "" };
  }
  if (b.stage === "auction" && b.auction) return { title: `Auktion over ${sqName(b.auction.sq)}`, hint: `${names(b.auction.cur)} byder…` };
  if (b.stage === "trade" && b.trade) return { title: `${names(b.trade.from)} foreslår en handel`, hint: "" };
  return { title: `${names(b.actor)} er i gang…`, hint: "" };
}

export function Monopoly(p: GameProps) {
  const { view, act, answer, busy, feedback, clearFeedback } = p;
  const st = view.state;
  const b: MonopolyView = st.board;
  const you: number = view.you;
  const over = st.phase === "over";
  const me = b.players[you]!;
  const myTurn = b.actor === you && !over;
  const hasQuestion = !!st.question;
  const names = useCallback((seat: number) => view.players.find((x: any) => x.seat === seat)?.username ?? `Spiller ${seat + 1}`, [view.players]);
  const go = useCallback((a: any) => { void act(a); }, [act]);

  /* ── the way the board is shown ── */
  const [failed, setFailed] = useState(false);
  const [force2d, setForce2d] = useState(readForce2d);
  const mode3d = !failed && !force2d;
  const [stage, setStage] = useState<MonoStage | null>(null);
  const [stageBusy, setStageBusy] = useState(false);
  useEffect(() => { if (!mode3d) setStageBusy(false); }, [mode3d]);
  const held = useHold(stageBusy && mode3d, 9000);            // the board is still playing a move
  const heldQ = useHold(stageBusy && mode3d, 1800);           // the question waits for the board – but not for long (the clock runs on the server)
  const wide = useMedia("(min-width: 1100px)");
  const roomy = useMedia("(min-width: 760px)");

  /* ── selection, hover, who is unfolded ── */
  const [sel, setSel] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [openSeat, setOpenSeat] = useState<number | null>(null);
  const [, setFollowTick] = useState(0);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [mode, setMode] = useState<Mode | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const hintDone = useRef(false);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /* ── sound ── */
  const snd = useRef<MonoSound | null>(null);
  if (!snd.current) snd.current = new MonoSound(soundPref(view.mode === "class"));
  const [sndOn, setSndOn] = useState(snd.current.on);
  useEffect(() => {
    const s = snd.current!;
    const un = () => { if (s.on) s.unlock(); };
    window.addEventListener("pointerdown", un);
    window.addEventListener("keydown", un);
    return () => { window.removeEventListener("pointerdown", un); window.removeEventListener("keydown", un); s.dispose(); clearTimeout(hintTimer.current); };
  }, []);
  const toggleSound = () => { const s = snd.current!; s.setOn(!s.on); setSndOn(s.on); saveSoundPref(s.on); if (s.on) s.play("pop"); };
  const sfx = useCallback((n: SfxName) => snd.current?.play(n), []);

  /* ── the cast, the colours ── */
  const players: StagePlayer[] = useMemo(() => b.players.map((_, seat) => {
    const vp = view.players.find((x: any) => x.seat === seat);
    return { seat, name: vp?.username ?? `Spiller ${seat + 1}`, spec: specFromEquipped(vp?.avatar ?? {}), color: SEAT_COLOR[seat]! };
  }), [view.players, b.players.length]);
  const mineP = view.players.find((x: any) => x.seat === you);
  const palette = useMemo(() => paletteOf(mineP?.avatar), [mineP?.avatar]);

  /* ── what can be done on the board ── */
  const canManage = myTurn && !hasQuestion && !held && ["roll", "manage", "debt", "buy"].includes(b.stage);
  const rollOk = myTurn && b.stage === "roll" && !b.rolled && !hasQuestion && !held && !busy && me.jail === 0;
  const rollRef = useRef(false); rollRef.current = rollOk;
  const goRef = useRef(go); goRef.current = go;
  const ownsAny = b.owner.some((o) => o === you);
  const activeMode = mode && canManage && b.options[mode].length > 0 ? mode : null;
  useEffect(() => { if (mode && !activeMode) setMode(null); }, [mode, activeMode]);
  useEffect(() => {
    if (!mode) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") setMode(null); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [mode]);
  const pickTiles = useMemo(() => {
    if (!canManage) return null;
    if (activeMode) return b.options[activeMode];
    if (b.stage === "debt") return [...new Set([...b.options.sell, ...b.options.mortgage])];
    if (b.stage === "roll" || b.stage === "manage") return b.options.build;
    return null;
  }, [canManage, activeMode, b.stage, b.options]);
  const pickColor = activeMode ? MODE_COLOR[activeMode] : b.stage === "debt" ? "#ff9a3c" : "#22e06b";
  const focusTile = over ? null : b.q ? b.q.sq : b.stage === "buy" && myTurn ? me.pos : b.stage === "auction" && b.auction ? b.auction.sq : null;

  /* ── clear the feedback when the next question comes ── */
  useEffect(() => { if (st.question) clearFeedback(); /* eslint-disable-next-line */ }, [st.question?.pid]);

  /* ── the card that was drawn, in words ── */
  const cardKey = b.lastCard ? `${b.lastCard.deck}|${b.lastCard.seat}|${b.lastCard.text}` : "";
  const seenCard = useRef(cardKey);
  const [toast, setToast] = useState<{ deck: "chance" | "chest"; text: string; seat: number } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!b.lastCard || held || cardKey === seenCard.current) return;
    seenCard.current = cardKey;
    setToast(b.lastCard);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 7500);
  }, [cardKey, held]);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* what has just happened with money, bricks or the bank – as a line on the board */
  const lastFeed = b.feed[b.feed.length - 1];
  const feedKey = lastFeed ? `${lastFeed.seat}|${lastFeed.text}` : "";
  const seenFeed = useRef(feedKey);
  const [tick, setTick] = useState<{ seat: number | null; text: string } | null>(null);
  const tickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!lastFeed || held || feedKey === seenFeed.current) return;
    seenFeed.current = feedKey;
    if (!/betaler|modtager|køber|bygger|sælger|pantsætter|indløser|konkurs|vinder|auktion|handel|fængsel|START/i.test(lastFeed.text)) return;
    setTick(lastFeed);
    clearTimeout(tickTimer.current);
    tickTimer.current = setTimeout(() => setTick(null), 5000);
  }, [feedKey, held]);
  useEffect(() => () => clearTimeout(tickTimer.current), []);

  /* ── board handlers (the stage keeps the newest ones) ── */
  const pickTile = (t: number) => {
    if (activeMode && b.options[activeMode].includes(t)) { go({ type: activeMode, sq: t }); if (activeMode === "build") setMode(null); return; }
    setSel((c) => (c === t ? null : t));
  };
  const handlers = {
    onHover: setHover,
    onPick: pickTile,
    onTokenPick: (seat: number) => { setOpenSeat(seat); stage?.focusSeat(seat); },
    onDiceClick: () => { if (rollRef.current) goRef.current({ type: "roll" }); },
    onBusy: setStageBusy,
    sfx,
    onWheelHint: () => {
      if (hintDone.current) return;
      hintDone.current = true; setHint("Ctrl + rul (eller knib med to fingre) for at zoome");
      hintTimer.current = setTimeout(() => setHint(null), 3200);
    },
  };
  const insets = roomy ? { left: 0, right: 0, top: 76, bottom: 124 } : { left: 0, right: 0, top: 104, bottom: 206 };
  const toggle2d = () => { const n = !force2d; setForce2d(n); setFailed(false); try { localStorage.setItem(FORCE2D_KEY, n ? "1" : "0"); } catch { /* ignore */ } };

  const base = headlineOf({ b, you, names, over, winner: st.winner, hasQuestion, held });
  const { title, hint: sub } = activeMode ? { title: `${MODE_LABEL[activeMode]}: vælg et felt`, hint: modeHelp(activeMode) } : base;
  const shown = hover ?? sel;
  const playersList = b.players.map((_, s) => s);
  const others = playersList.filter((s) => s !== you);
  const chip = (s: number, compact = false) => (
    <PlayerChip key={s} b={b} p={view.players.find((x: any) => x.seat === s)} seat={s} you={you} names={names} held={held} open={openSeat === s} compact={compact}
      onToggle={() => { setOpenSeat((c) => (c === s ? null : s)); if (openSeat !== s) stage?.focusSeat(s); }} onTile={(i) => { setSel(i); }} />
  );
  const qReady = hasQuestion && !heldQ;
  const showModes = canManage && ownsAny && !over;
  const modeBar = showModes ? <ModeBar b={b} mode={activeMode} onMode={setMode} disabled={busy} /> : null;

  return (
    <div className={`m3 ${wide ? "wide" : "narrow"} ${roomy ? "roomy" : "tight"} ${hasQuestion ? "has-q" : ""}`} data-mode={mode3d ? "3d" : "2d"}>
      <div className="m3-main">
        <div className="m3-stage">
          {mode3d ? (
            <Stage3D b={b} over={over} winner={st.winner} players={players} palette={palette} rollInvite={rollOk} pickTiles={pickTiles} pickColor={pickColor} selected={sel} focusTile={focusTile}
              active={over ? null : b.actor} insets={insets} handlers={handlers} onReady={setStage}
              onFail={(why) => { setFailed(true); setStage(null); if (why === "lost") setHint("Grafikken blev afbrudt – du spiller videre med den enkle udgave."); }} />
          ) : (
            <div className="m3-flat"><div className="mono-board-wrap"><Board2D b={b} you={you} myTurn={myTurn} names={names} selected={sel} onPick={pickTile} pick={{ tiles: pickTiles, color: pickColor }} /></div></div>
          )}
          {mode3d && !stage && <div className="m3-loading" role="status"><LuLandmark size={30} aria-hidden="true" /><b>Gør brættet klart…</b><span className="m3-spin" aria-hidden="true" /></div>}

          {/* top: the players */}
          <div className="m3-top">{[you, ...others].map((s) => chip(s, !roomy))}</div>

          {/* the card that was drawn, what just happened, hints */}
          <div className="m3-toasts" aria-live="polite">
            {toast && (
              <button type="button" className={`m3-card ${toast.deck}`} onClick={() => setToast(null)} aria-label="Luk kortet">
                <small>{toast.deck === "chance" ? "Chancen" : "Fælleskassen"} · {names(toast.seat)}</small>
                <b>{toast.text}</b>
              </button>
            )}
            {tick && !toast && <div className="m3-tick" style={{ "--seat": tick.seat !== null ? SEAT_COLOR[tick.seat] : "#2f7bff" }}>{tick.seat !== null && <b>{names(tick.seat)} </b>}{tick.text}</div>}
            {hint && <div className="m3-hint" role="status"><LuMousePointerClick size={16} aria-hidden="true" /> {hint}</div>}
            {!mode3d && failed && <div className="m3-hint warn" role="status">3D kunne ikke startes på din enhed – du ser den enkle udgave.</div>}
          </div>

          {/* the tools, the actions */}
          <div className="m3-tools" role="toolbar" aria-label="Visning">
            {mode3d && (
              <>
                <button type="button" className="m3-tool zoom" onClick={() => stage?.zoomBy(0.85)} aria-label="Zoom ind" title="Zoom ind"><LuZoomIn size={18} aria-hidden="true" /></button>
                <button type="button" className="m3-tool zoom" onClick={() => stage?.zoomBy(1 / 0.85)} aria-label="Zoom ud" title="Zoom ud"><LuZoomOut size={18} aria-hidden="true" /></button>
                <button type="button" className="m3-tool" onClick={() => { stage?.resetCamera(); setOpenSeat(null); }} aria-label="Nulstil kamera" title="Nulstil kamera"><LuRotateCcw size={18} aria-hidden="true" /></button>
                <button type="button" className="m3-tool" onClick={() => { if (stage) stage.follow = !stage.follow; setFollowTick((n) => n + 1); }} aria-pressed={stage?.follow ?? true} aria-label="Kameraet følger brikken" title="Kameraet følger brikken"><LuLocateFixed size={18} aria-hidden="true" /></button>
              </>
            )}
            <span className="m3-tool info" title={`Runde ${b.round} af ${b.rounds}. Banken har ${b.supply.houses} huse og ${b.supply.hotels} hoteller tilbage.`} role="img" aria-label={`Runde ${b.round} af ${b.rounds}`}><LuDices size={16} aria-hidden="true" /><span>{b.round}/{b.rounds}</span></span>
            <button type="button" className="m3-tool" onClick={toggleSound} aria-pressed={sndOn} aria-label={sndOn ? "Slå lyden fra" : "Slå lyden til"} title={sndOn ? "Slå lyden fra" : "Slå lyden til"}>{sndOn ? <LuVolume2 size={18} aria-hidden="true" /> : <LuVolumeX size={18} aria-hidden="true" />}</button>
            <button type="button" className="m3-tool text" onClick={toggle2d} aria-label={mode3d ? "Skift til enkel 2D-visning" : "Skift til 3D-visning"} title={mode3d ? "Enkel 2D-visning (til langsomme enheder)" : "3D-visning"}>{mode3d ? <LuGrid3X3 size={16} aria-hidden="true" /> : <LuBox size={16} aria-hidden="true" />}<span>{mode3d ? "2D" : "3D"}</span></button>
          </div>
          {roomy && modeBar}
          <div className="m3-bottom">
            {!roomy && modeBar}
            <div className="m3-panel">
              <div className="m3-say" role="status" aria-live="polite">
                <b>{title}</b>
                {sub && <span className="hint">{sub}</span>}
              </div>
              <ActionBar b={b} you={you} over={over} hasQuestion={hasQuestion} busy={busy} held={held} go={go} names={names} onTrade={() => setTradeOpen(true)} />
            </div>
          </div>
        </div>
      </div>

      {/* the right-hand column: the question, the field, my properties, what happened */}
      <aside className="m3-side" aria-label="EduPoly – spørgsmål og ejendomme">
        <section className={`m3-prompt ${hasQuestion || feedback ? "active" : ""}`} aria-label="Spørgsmål">
          {hasQuestion && !qReady && <div className="m3-qwait"><span className="m3-spin" aria-hidden="true" /> Spørgsmålet gøres klar…</div>}
          {qReady && <QuestionCard q={st.question} serverNow={st.now} onAnswer={answer} busy={busy} totalMs={(st.timerSec ?? 30) * 1000} />}
          {!hasQuestion && feedback && <FeedbackBox fb={feedback} />}
          {!hasQuestion && !feedback && b.stage === "trade" && !myTurn && b.trade && !over && <TradeSummary t={b.trade} names={names} />}
          {!hasQuestion && !feedback && !over && <p className="m3-idle"><LuCircleHelp size={16} aria-hidden="true" /> Spørgsmål dukker op, når du lander, skal betale eller vil bygge.</p>}
        </section>

        {wide && (
          <section className="m3-deedslot" aria-label="Valgt felt">
            {shown !== null ? <DeedCard b={b} i={shown} names={names} you={you} canManage={canManage} busy={busy} go={go} onClose={sel !== null ? () => setSel(null) : undefined} />
              : <div className="m3-deed-empty"><LuMousePointerClick size={22} aria-hidden="true" /><b>Se et felt nærmere</b><span>Før musen hen over brættet – eller klik på et felt – for at se pris, leje og ejer.</span></div>}
          </section>
        )}

        <section className="card m3-card-panel" aria-label="Dine ejendomme">
          <div className="panel-title">Dine ejendomme</div>
          <PropertyList b={b} you={you} canManage={canManage} busy={busy || held} go={go} selected={sel} onSelect={(i) => setSel(i)} />
        </section>

        <section className="card m3-card-panel" aria-label="Hvad er sket">
          <div className="panel-title">Hvad er sket?</div>
          <small className="muted m3-bank"><LuBuilding2 size={14} aria-hidden="true" /> Banken har {b.supply.houses} huse og {b.supply.hotels} hoteller tilbage.</small>
          <ol className="m3-feed" reversed>
            {[...b.feed].reverse().map((f, k) => <li key={k}>{f.seat !== null ? <b style={{ color: SEAT_COLOR[f.seat] }}>{names(f.seat)} </b> : null}{f.text}</li>)}
          </ol>
        </section>
      </aside>

      {/* narrow screens: the field as a sheet over the board */}
      {!wide && sel !== null && !hasQuestion && (
        <div className="m3-sheet" role="dialog" aria-label="Valgt felt">
          <DeedCard b={b} i={sel} names={names} you={you} canManage={canManage} busy={busy} go={go} onClose={() => setSel(null)} />
        </div>
      )}

      {tradeOpen && <TradeModal b={b} you={you} names={names} onClose={() => setTradeOpen(false)} onSend={(t: any) => { setTradeOpen(false); go({ type: "propose", ...t }); }} />}
    </div>
  );
}

function TradeModal({ b, you, names, onClose, onSend }: { b: MonopolyView; you: number; names: (s: number) => string; onClose: () => void; onSend: (t: any) => void }) {
  const others = b.players.map((_, s) => s).filter((s) => s !== you && !b.players[s]!.out);
  const [to, setTo] = useState(others[0]!);
  const [give, setGive] = useState<number[]>([]);
  const [get, setGet] = useState<number[]>([]);
  const [giveCash, setGiveCash] = useState(0);
  const [getCash, setGetCash] = useState(0);
  const tradeable = (seat: number) => b.owner.map((o, i) => (o === seat ? i : -1)).filter((i) => i >= 0 && !(SQUARES[i]!.t === "prop" && GROUPS[(SQUARES[i] as any).group as keyof typeof GROUPS].some((x) => b.houses[x]! > 0)));
  const toggle = (arr: number[], set: (a: number[]) => void, i: number) => set(arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i]);
  const mine = useMemo(() => tradeable(you), [b]);
  const theirs = useMemo(() => tradeable(to), [b, to]);
  const empty = !give.length && !get.length && !giveCash && !getCash;
  return (
    <Modal title="Byt ejendomme" onClose={onClose}>
      <div className="stack">
        <label className="field">Hvem vil du handle med?
          <select value={to} onChange={(e: any) => { setTo(Number(e.target.value)); setGet([]); }}>{others.map((s) => <option key={s} value={s}>{names(s)}</option>)}</select>
        </label>
        <div className="grid cols-2">
          <fieldset className="trade-col"><legend>Du giver</legend>
            {mine.length === 0 && <small className="muted">Ingen ejendomme (sælg bygninger først).</small>}
            {mine.map((i) => <label key={i} className="check"><input type="checkbox" checked={give.includes(i)} onChange={() => toggle(give, setGive, i)} /><span>{SQUARES[i]!.name}</span></label>)}
            <label className="field">Penge<input type="number" min={0} max={b.players[you]!.cash} value={giveCash} onChange={(e: any) => setGiveCash(Math.max(0, Number(e.target.value) || 0))} /></label>
          </fieldset>
          <fieldset className="trade-col"><legend>Du får</legend>
            {theirs.length === 0 && <small className="muted">Ingen ejendomme at handle med.</small>}
            {theirs.map((i) => <label key={i} className="check"><input type="checkbox" checked={get.includes(i)} onChange={() => toggle(get, setGet, i)} /><span>{SQUARES[i]!.name}</span></label>)}
            <label className="field">Penge<input type="number" min={0} max={b.players[to]!.cash} value={getCash} onChange={(e: any) => setGetCash(Math.max(0, Number(e.target.value) || 0))} /></label>
          </fieldset>
        </div>
        <div className="row">
          <button className="btn cta" disabled={empty} onClick={() => onSend({ to, giveProps: give, giveCash, getProps: get, getCash })}>Send tilbud</button>
          <button className="btn ghost" onClick={onClose}>Annullér</button>
        </div>
        <small className="muted">Den anden spiller kan acceptere eller afslå. Pantsatte ejendomme koster 10 % rente for køberen.</small>
      </div>
    </Modal>
  );
}

